import { useState, useEffect } from 'preact/hooks';
import type { JSX } from 'preact';

// React/Preact type conflicts are resolved via type declarations in src/types/preact-react.d.ts
import './styles/status-line.css';
import Terminal from './components/Terminal';
import { PersistentTerminal } from './components/PersistentTerminal';
import { StatusLine } from './components/StatusLine';
import { ProjectOverview } from './components/ProjectOverview';
import { Chat } from './components/Chat';
import { Todos } from './components/Todos';
import { SDKStatus } from './components/SDKStatus';
import { AppSidebar } from './components/app-sidebar';
import { SessionManager } from './components/SessionManager';
import { SessionHistory } from './components/SessionHistory';
import { SettingsPanel } from './components/SettingsPanel';
import { GitHubBrowser } from './components/GitHubBrowser';
import SubagentManager from './components/SubagentManager';
import { FileExplorer } from './components/FileExplorer';
import { CodeEditor } from './components/CodeEditor';
import { ProjectWorkspace } from './components/ProjectWorkspace';
import { useAuth } from './hooks/useAuth';
import { useClaudeCodeAuth } from './hooks/useClaudeCodeAuth';
import { useWebSocket } from './hooks/useWebSocket';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './components/ui/breadcrumb';
import { Separator } from './components/ui/separator';
import type { ClaudeCodeSession, GitHubRepo, Project, ProjectFile, Subagent } from '../../shared/types';

export function App() {
  const { isAuthenticated, user, login, logout } = useAuth();
  const { 
    isAuthenticated: isClaudeCodeAuthenticated, 
    user: claudeCodeUser, 
    subscription: claudeCodeSubscription,
    logout: claudeCodeLogout 
  } = useClaudeCodeAuth();
  const [currentSession, setCurrentSession] = useState<ClaudeCodeSession | null>(null);
  const [sessions, setSessions] = useState<ClaudeCodeSession[]>([]);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentFile, setCurrentFile] = useState<ProjectFile | null>(null);
  const [terminalOutput, setTerminalOutput] = useState<string>('');
  const [isTerminalConnected, setIsTerminalConnected] = useState<boolean>(false);
  const [todos, setTodos] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [subagents, setSubagents] = useState<Subagent[]>([]);
  const [currentPage, setCurrentPage] = useState('projects');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [projectView, setProjectView] = useState<'chat' | 'todos' | 'overview' | 'files' | 'sessions' | 'agents' | 'settings'>('chat');
  const { connect, disconnect, sendMessage, messages } = useWebSocket();

  // Use Byte DevOps authentication if available, otherwise fall back to custom auth
  const effectiveAuth = isClaudeCodeAuthenticated ? {
    isAuthenticated: true,
    user: claudeCodeUser,
    logout: claudeCodeLogout
  } : {
    isAuthenticated,
    user,
    logout
  };

  useEffect(() => {
    if (effectiveAuth.isAuthenticated) {
      // Connect to WebSocket for real-time updates
      connect();
      // Load subagents from filesystem
      loadSubagents();
    } else {
      disconnect();
    }

    return () => disconnect();
  }, [effectiveAuth.isAuthenticated]);

  const loadSessions = async () => {
    try {
      const response = await fetch('/api/sessions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadRepositories = async () => {
    try {
      const response = await fetch('/api/github/repos', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRepos(data);
      }
    } catch (error) {
      console.error('Failed to load repositories:', error);
    }
  };

  const createSession = async (name: string, githubRepo?: string) => {
    try {
      const response = await fetch('/api/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name, githubRepo }),
      });

      if (response.ok) {
        const data = await response.json();
        await loadSessions();
        return data.sessionId;
      }
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const deleteSession = async (sessionId: string) => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await loadSessions();
        if (currentSession?.sessionId === sessionId) {
          setCurrentSession(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const resumeSession = async (sessionId: string) => {
    try {
      const response = await fetch(`/api/sessions/${sessionId}/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const session = sessions.find(s => s.sessionId === sessionId);
        if (session) {
          setCurrentSession(session);
          setCurrentPage('terminal');
        }
      }
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  };

  const duplicateSession = async (session: ClaudeCodeSession) => {
    try {
      const newName = `${session.sessionId} (Copy)`;
      await createSession(newName, session.cwd);
    } catch (error) {
      console.error('Failed to duplicate session:', error);
    }
  };

  const sendClaudeQuery = async (prompt: string, sessionId?: string) => {
    try {
      // Instead of making HTTP requests, we should run the actual Byte DevOps CLI
      // For now, we'll use a simple approach that simulates the CLI behavior
      
      if (prompt === '/login') {
        return {
          success: true,
          message: '🔐 **Authentication Required**\n\nTo use Byte DevOps, you need to authenticate with your Anthropic account.\n\n**Option 1: Use Claude Console**\n1. Go to https://console.anthropic.com/\n2. Create an API key\n3. Set the ANTHROPIC_API_KEY environment variable\n\n**Option 2: Use Claude.ai**\n1. Go to https://claude.ai/\n2. Sign up for a subscription\n3. Use the web interface directly\n\n**Note:** This service requires a valid Anthropic API key to function.',
          cost: 0,
          duration: 100
        };
      }
      
      if (prompt === '/help') {
        return {
          success: true,
          message: '📚 **Available Commands**\n\n- `/login` - Authentication information\n- `/help` - Show this help message\n- `/status` - Show current status\n- Ask any coding question or request\n\n**Examples:**\n- "Create a React component for user login"\n- "Fix the bug in my authentication code"\n- "Explain how this function works"\n- "Write unit tests for this module"',
          cost: 0,
          duration: 50
        };
      }
      
      if (prompt === '/status') {
        return {
          success: true,
          message: '📊 **Service Status**\n\n✅ Byte DevOps: Running\n✅ Anthropic API: Connected\n✅ Session: Active\n\n**Current Configuration:**\n- Model: claude-3-5-sonnet-20241022\n- Session ID: ' + (sessionId || 'new') + '\n- Status: Ready to help with coding tasks',
          cost: 0,
          duration: 50
        };
      }
      
      // For other queries, we would normally run the actual Byte DevOps CLI
      // For now, return a placeholder response
      return {
        success: true,
        message: `🤖 **Byte DevOps Response**\n\nI received your query: "${prompt}"\n\nTo fully integrate with Byte DevOps CLI, we need to:\n1. Run the actual \`claude\` command\n2. Send commands to it\n3. Capture its responses\n\nThis is a placeholder response. The actual implementation would connect to the real Byte DevOps CLI.`,
        cost: 0,
        duration: 200
      };
    } catch (error) {
      console.error('Claude query error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };

  const handleSettingsChange = async (settings: any) => {
    try {
      // In a real implementation, this would save settings to the API
      console.log('Settings changed:', settings);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleSaveSettings = async (settings: any) => {
    try {
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        console.log('Settings saved successfully');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleRepoSelect = (repo: GitHubRepo) => {
    console.log('Repository selected:', repo);
  };

  const handleRepoClone = async (repo: GitHubRepo) => {
    try {
      const response = await fetch('/api/github/clone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ repoUrl: repo.cloneUrl }),
      });

      if (response.ok) {
        console.log('Repository cloned successfully');
      }
    } catch (error) {
      console.error('Failed to clone repository:', error);
    }
  };

  const handleRepoSync = async (repo: GitHubRepo) => {
    try {
      const response = await fetch('/api/github/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ repoId: repo.id }),
      });

      if (response.ok) {
        console.log('Repository synced successfully');
      }
    } catch (error) {
      console.error('Failed to sync repository:', error);
    }
  };

  // Project Management Handlers
  const handleTerminalCommand = (command: string) => {
    if (sendMessage) {
      sendMessage({ type: 'query', data: command });
    }
  };

  // Update terminal output when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (latestMessage.type === 'response' || latestMessage.type === 'status') {
        const newOutput = latestMessage.data + '\n';
        setTerminalOutput(prev => prev + newOutput);
        
        // Parse todos from the output
        parseTodosFromTerminalOutput(newOutput);
        
        // Add assistant messages to chat
        if (latestMessage.type === 'response') {
          const assistantMessage = {
            id: `msg-${Date.now()}`,
            type: 'assistant',
            content: latestMessage.data,
            timestamp: new Date()
          };
          setChatMessages(prev => [...prev, assistantMessage]);
        }
      }
    }
  }, [messages]);

  // Parse todos from terminal output
  const parseTodosFromTerminalOutput = (output: string) => {
    // Look for TodoWrite tool usage patterns
    const todoPatterns = [
      /TodoWrite.*?todos.*?\[(.*?)\]/gs,
      /"todos":\s*\[(.*?)\]/gs,
      /TODO.*?\[(.*?)\]/gs
    ];

    for (const pattern of todoPatterns) {
      const matches = output.match(pattern);
      if (matches) {
        try {
          matches.forEach(match => {
            const todoMatch = match.match(/\[(.*?)\]/);
            if (todoMatch) {
              const todosData = JSON.parse(`[${todoMatch[1]}]`);
              const parsedTodos = todosData.map((todo: any, index: number) => ({
                id: `todo-${Date.now()}-${index}`,
                content: todo.content || todo.text || '',
                status: todo.status || 'pending',
                category: todo.category || 'General',
                priority: todo.priority || 'medium',
                activeForm: todo.activeForm || ''
              }));
              setTodos(prev => [...prev, ...parsedTodos]);
            }
          });
        } catch (error) {
          console.error('Failed to parse todos:', error);
        }
      }
    }
  };

  // Monitor WebSocket connection status
  useEffect(() => {
    setIsTerminalConnected(connect !== null);
  }, [connect]);

  const handleProjectSelect = (project: any) => {
    setCurrentProject(project);
    if (project) {
      setProjectView('chat');
      // Fetch project-specific data
      loadProjectData(project.id);
    } else {
      setCurrentPage('projects');
    }
  };

  const loadProjectData = async (projectId: string) => {
    try {
      // Load project-specific sessions
      const sessionsResponse = await fetch(`/api/projects/${projectId}/sessions`);
      if (sessionsResponse.ok) {
        const projectSessions = await sessionsResponse.json();
        setSessions(projectSessions);
      }

      // Load project-specific subagents
      const subagentsResponse = await fetch(`/api/projects/${projectId}/subagents`);
      if (subagentsResponse.ok) {
        const projectSubagents = await subagentsResponse.json();
        setSubagents(projectSubagents);
      }

      // Load project files
      const filesResponse = await fetch(`/api/projects/${projectId}/files`);
      if (filesResponse.ok) {
        const projectFiles = await filesResponse.json();
        // Handle project files if needed
      }
    } catch (error) {
      console.error('Failed to load project data:', error);
    }
  };

  const handleProjectCreate = async (projectData: Omit<Project, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(projectData),
      });

      if (response.ok) {
        await loadProjects();
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleProjectUpdate = async (id: string, projectData: any) => {
    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(projectData),
      });

      if (response.ok) {
        await loadProjects();
      }
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const handleProjectDelete = async (id: string) => {
    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await loadProjects();
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  const handleProjectClone = async (project: any) => {
    try {
      const response = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...project,
          name: `${project.name} (Copy)`,
        }),
      });

      if (response.ok) {
        await loadProjects();
      }
    } catch (error) {
      console.error('Failed to clone project:', error);
    }
  };

  const handleProjectExport = async (project: any) => {
    try {
      const response = await fetch(`/api/projects/${project.id}/export`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project.name}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export project:', error);
    }
  };

  const handleProjectImport = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/projects/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (response.ok) {
        await loadProjects();
      }
    } catch (error) {
      console.error('Failed to import project:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  // File Management Handlers
  const handleFileSelect = (file: ProjectFile) => {
    // Add projectId to the file
    const fileWithProject = { ...file, projectId: currentProject?.id };
    setCurrentFile(fileWithProject);
    setCurrentPage('editor');
  };

  const handleFileEdit = (file: ProjectFile) => {
    // Add projectId to the file
    const fileWithProject = { ...file, projectId: currentProject?.id };
    setCurrentFile(fileWithProject);
    setCurrentPage('editor');
  };

  const handleFileCreate = async (path: string, isDirectory: boolean) => {
    if (!currentProject) return;

    try {
      const response = await fetch('/api/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          projectId: currentProject.id,
          path,
          isDirectory,
        }),
      });

      if (response.ok) {
        // Refresh file list
      }
    } catch (error) {
      console.error('Failed to create file:', error);
    }
  };

  const handleFileDelete = async (fileId: string) => {
    try {
      const response = await fetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        // Refresh file list
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handleFileRename = async (fileId: string, newPath: string) => {
    try {
      const response = await fetch(`/api/files/${fileId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ path: newPath }),
      });

      if (response.ok) {
        // Refresh file list
      }
    } catch (error) {
      console.error('Failed to rename file:', error);
    }
  };

  const handleFileMove = async (fileId: string, newPath: string) => {
    try {
      const response = await fetch(`/api/files/${fileId}/move`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ newPath }),
      });

      if (response.ok) {
        // Refresh file list
      }
    } catch (error) {
      console.error('Failed to move file:', error);
    }
  };

  const handleFileCopy = async (fileId: string, newPath: string) => {
    try {
      const response = await fetch(`/api/files/${fileId}/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ newPath }),
      });

      if (response.ok) {
        // Refresh file list
      }
    } catch (error) {
      console.error('Failed to copy file:', error);
    }
  };

  const handleFileSave = async (filePath: string, content: string) => {
    if (!currentProject) {
      console.error('No current project selected');
      return;
    }

    try {
      const response = await fetch(`/api/projects/${currentProject.id}/files/${filePath}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('File saved successfully:', result);
        
        // Update current file content
        if (currentFile && currentFile.path === filePath) {
          setCurrentFile({ ...currentFile });
        }
        
        return { success: true, message: result.message };
      } else {
        const error = await response.json();
        console.error('Failed to save file:', error);
        return { success: false, error: error.error || 'Failed to save file' };
      }
    } catch (error) {
      console.error('Failed to save file:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // Subagent Management Handlers
  const handleSubagentCreate = async (subagentData: Omit<Subagent, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const response = await fetch('/api/subagents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subagentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subagent');
      }

      await loadSubagents();
    } catch (error) {
      console.error('Failed to create subagent:', error);
      throw error;
    }
  };

  const handleSubagentUpdate = async (id: string, subagentData: Partial<Subagent>) => {
    try {
      const subagent = subagents.find(s => s.id === id);
      if (!subagent) {
        throw new Error('Subagent not found');
      }

      const response = await fetch(`/api/subagents/${subagent.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subagentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update subagent');
      }

      await loadSubagents();
    } catch (error) {
      console.error('Failed to update subagent:', error);
      throw error;
    }
  };

  const handleSubagentDelete = async (id: string) => {
    try {
      const subagent = subagents.find(s => s.id === id);
      if (!subagent) {
        throw new Error('Subagent not found');
      }

      const response = await fetch(`/api/subagents/${subagent.name}?type=${subagent.type}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete subagent');
      }

      await loadSubagents();
    } catch (error) {
      console.error('Failed to delete subagent:', error);
      throw error;
    }
  };

  const handleSubagentExecute = async (id: string, prompt: string) => {
    try {
      const subagent = subagents.find(s => s.id === id);
      if (!subagent) {
        throw new Error('Subagent not found');
      }

      // For now, we'll execute through the terminal
      // In the future, this could be a direct SDK call
      console.log(`Executing subagent ${subagent.name} with prompt: ${prompt}`);
      
      // You could implement direct SDK execution here
      // For now, we'll just log the execution
      alert(`Subagent ${subagent.name} execution requested with prompt: ${prompt}`);
    } catch (error) {
      console.error('Failed to execute subagent:', error);
      throw error;
    }
  };

  const loadSubagents = async () => {
    try {
      const response = await fetch('/api/subagents');

      if (!response.ok) {
        throw new Error('Failed to load subagents');
      }

      const data = await response.json();
      setSubagents(data);
    } catch (error) {
      console.error('Failed to load subagents:', error);
    }
  };

  const getPageTitle = () => {
    if (!currentProject) {
      return 'Projects';
    }
    
    switch (projectView) {
      case 'chat': return `${currentProject.name} - Chat`;
      case 'todos': return `${currentProject.name} - Todos`;
      case 'overview': return `${currentProject.name} - Overview`;
      case 'files': return `${currentProject.name} - Files`;
      case 'sessions': return `${currentProject.name} - Sessions`;
      case 'agents': return `${currentProject.name} - Agents`;
      case 'settings': return `${currentProject.name} - Settings`;
      default: return `${currentProject.name} - Chat`;
    }
  };

  const renderPage = () => {
    // If no project is selected, show project list
    if (!currentProject) {
      return (
        <ProjectWorkspace
          selectedProject={null}
          onProjectSelect={handleProjectSelect}
          onProjectCreate={handleProjectCreate}
          onProjectUpdate={handleProjectUpdate}
          onProjectDelete={handleProjectDelete}
          onProjectClone={handleProjectClone}
          onProjectExport={handleProjectExport}
          onProjectImport={handleProjectImport}
        />
      );
    }

    // If project is selected, show project-specific content
    switch (projectView) {
      case 'chat':
        return (
          <Chat
            projectId={currentProject.id}
            projectName={currentProject.name}
            onTerminalCommand={handleTerminalCommand}
          />
        );
      case 'todos':
        return (
          <Todos
            projectId={currentProject.id}
            projectName={currentProject.name}
          />
        );
      case 'overview':
        return (
          <ProjectOverview
            projectId={currentProject.id}
            projectName={currentProject.name}
          />
        );
      case 'files':
        return (
          <div className="flex h-full">
            <div className="w-1/3 border-r">
              <FileExplorer
                projectId={currentProject.id}
                onFileSelect={handleFileSelect}
                onFileEdit={handleFileEdit}
                onFileCreate={handleFileCreate}
                onFileDelete={handleFileDelete}
                onFileRename={handleFileRename}
                onFileMove={handleFileMove}
                onFileCopy={handleFileCopy}
              />
            </div>
            <div className="flex-1">
              {currentFile ? (
                <CodeEditor
                  file={currentFile}
                  onSave={handleFileSave}
                  onClose={() => setCurrentFile(null)}
                  isFullscreen={isFullscreen}
                  onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <p>Select a file to start editing</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'sessions':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Sessions - {currentProject.name}</h2>
                <p className="text-muted-foreground">Manage Byte DevOps sessions for this project</p>
              </div>
            </div>
            <SessionManager
              sessions={sessions}
              currentSession={currentSession}
              onSessionSelect={setCurrentSession}
              onCreateSession={createSession}
              onLoadSessions={loadSessions}
              onDeleteSession={deleteSession}
              onResumeSession={resumeSession}
            />
          </div>
        );
      case 'agents':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Agents - {currentProject.name}</h2>
                <p className="text-muted-foreground">Manage subagents for this project</p>
              </div>
            </div>
            <SubagentManager
              sessionId={currentSession?.sessionId}
              onSubagentCreate={handleSubagentCreate}
              onSubagentUpdate={handleSubagentUpdate}
              onSubagentDelete={handleSubagentDelete}
              onSubagentExecute={handleSubagentExecute}
            />
          </div>
        );
      case 'settings':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Settings - {currentProject.name}</h2>
                <p className="text-muted-foreground">Configure project settings</p>
              </div>
            </div>
            <SDKStatus
              projectId={currentProject.id}
              projectPath={currentProject.path || currentProject.workingDirectory || ''}
            />
            <SettingsPanel
              session={currentSession}
              onSettingsChange={handleSettingsChange}
              onSaveSettings={handleSaveSettings}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar 
        user={effectiveAuth.user ? {
          name: (effectiveAuth.user as any).name || effectiveAuth.user.email || 'User',
          email: effectiveAuth.user.email,
          avatar: (effectiveAuth.user as any).avatar,
          organization: (effectiveAuth.user as any).organization,
          organizationRole: (effectiveAuth.user as any).organizationRole
        } : undefined}
        subscription={isClaudeCodeAuthenticated ? claudeCodeSubscription : undefined}
        sessions={sessions.map(session => ({
          sessionId: session.sessionId,
          status: 'active'
        }))}
        currentSession={currentSession?.sessionId}
        isAuthenticated={effectiveAuth.isAuthenticated}
        onSessionSelect={(sessionId) => {
          const session = sessions.find(s => s.sessionId === sessionId);
          if (session) {
            setCurrentSession(session);
            setCurrentPage('terminal');
          }
        }}
        onPageChange={(page) => {
          if (page === 'projects') {
            setCurrentProject(null);
            setCurrentPage('projects');
          } else {
            setProjectView(page as any);
          }
        }}
        currentPage={projectView}
        onLogin={() => {
          // Redirect to terminal and show login instructions
          setCurrentPage('terminal');
        }}
        onLogout={effectiveAuth.logout}
        currentProject={currentProject ? {
          id: currentProject.id,
          name: currentProject.name,
          path: currentProject.path || currentProject.workingDirectory || ''
        } : null}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="#" onClick={() => {
                    setCurrentProject(null);
                    setCurrentPage('projects');
                  }}>
                    Byte DevOps
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                {currentProject ? (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink href="#" onClick={() => setProjectView('overview')}>
                        {currentProject.name}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        {projectView.charAt(0).toUpperCase() + projectView.slice(1)}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                ) : (
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      {getPageTitle()}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <Separator orientation="vertical" className="mr-auto h-4" />
          <div className="flex items-center gap-2 px-4">
            {currentProject && (
              <div className="flex items-center gap-2 mr-4">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm font-medium text-green-600">
                  {currentProject.name}
                </span>
              </div>
            )}
            <span className="text-sm text-muted-foreground">
              {effectiveAuth.isAuthenticated && effectiveAuth.user ? 
                `${effectiveAuth.user.email}` : 
                'Not authenticated - use /login in terminal'
              }
            </span>
          </div>
        </header>
        
        <div className={`flex flex-1 flex-col gap-4 p-4 pt-0 ${currentProject ? 'main-content-with-terminal' : ''}`}>
          {renderPage()}
        </div>
      </SidebarInset>
      
      {/* Persistent Terminal - Only show when project is selected */}
      {currentProject && (
        <PersistentTerminal
          currentProject={{
            id: currentProject.id,
            name: currentProject.name,
            path: currentProject.path || currentProject.workingDirectory || '',
            type: currentProject.type || 'project'
          }}
          onTerminalCommand={handleTerminalCommand}
          terminalOutput={terminalOutput}
          isTerminalConnected={isTerminalConnected}
        />
      )}
      
      {/* Fixed Status Line - Only show when project is selected */}
      {currentProject && (
        <StatusLine
          currentProject={{
            id: currentProject.id,
            name: currentProject.name,
            path: currentProject.path || currentProject.workingDirectory || '',
            type: currentProject.type || 'project'
          }}
          isTerminalConnected={isTerminalConnected}
          isAuthenticated={effectiveAuth.isAuthenticated}
          user={effectiveAuth.user}
          terminalOutput={terminalOutput}
          todos={todos}
          subagents={subagents}
          currentView={projectView}
        />
      )}
        

      </SidebarProvider>
  );
}